import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Download,
  Package,
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  ArrowLeft,
  Loader2,
  ImageIcon,
  Tag,
  DollarSign,
  Archive,
  Filter,
  Upload,
  CheckSquare,
  X,
  FolderOpen,
  Tags,
  AlertCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { isVersionConflict, conflictCurrentVersion, apiErrorMessage } from '@/lib/conflict';
import { RefreshingHint } from '@/components/common/DataState';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import BulkImportDialog from '@/components/ecommerce/BulkImportDialog';
import CategoryManagerDialog from '@/components/ecommerce/CategoryManagerDialog';

// ── Types ──

interface ProductImage {
  url: string;
  alt: string;
  order: number;
  isPrimary: boolean;
}

interface ProductVariant {
  id: string;
  name: string;
  sku: string;
  price: number;
  stock: number;
  attributes: Record<string, string>;
  isActive: boolean;
}

interface ProductSEO {
  metaTitle: string;
  metaDescription: string;
  ogImage: string;
}

interface Product {
  _id: string;
  /** Optimistic-concurrency counter from the server; absent (legacy doc) = 0. */
  version?: number;
  name: string;
  slug: string;
  description: string;
  shortDescription: string;
  type: 'physical' | 'digital' | 'service';
  status: 'draft' | 'active' | 'archived';
  categoryId: string;
  tags: string[];
  price: number;
  compareAtPrice: number;
  taxRate?: number;
  sku: string;
  stock: number;
  trackInventory: boolean;
  lowStockThreshold: number;
  hasVariants: boolean;
  variants: ProductVariant[];
  images: ProductImage[];
  seo: ProductSEO;
  viewCount: number;
  soldCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Category {
  _id: string;
  name: string;
  slug: string;
  description: string;
  imageUrl: string;
  parentId: string;
  order: number;
  isActive: boolean;
  productCount: number;
}

interface DashboardStats {
  totalProducts: number;
  activeProducts: number;
  totalOrders: number;
  revenue: number;
  lowStockCount: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface ProductFormData {
  name: string;
  slug: string;
  description: string;
  type: 'physical' | 'digital' | 'service';
  status: 'draft' | 'active';
  price: string;
  compareAtPrice: string;
  taxRate: string; // '' means inherit from store settings
  sku: string;
  stock: string;
  trackInventory: boolean;
  categoryId: string;
  tags: string;
}

const emptyForm: ProductFormData = {
  name: '',
  slug: '',
  description: '',
  type: 'physical',
  status: 'draft',
  price: '',
  compareAtPrice: '',
  taxRate: '', // empty = inherit store default
  sku: '',
  stock: '0',
  trackInventory: true,
  categoryId: '',
  tags: '',
};

// ── Helpers ──

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

const CURRENCIES: { code: string; symbol: string; label: string }[] = [
  { code: 'INR', symbol: '\u20b9', label: 'INR (\u20b9)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
  { code: 'EUR', symbol: '\u20ac', label: 'EUR (\u20ac)' },
  { code: 'GBP', symbol: '\u00a3', label: 'GBP (\u00a3)' },
  { code: 'JPY', symbol: '\u00a5', label: 'JPY (\u00a5)' },
  { code: 'AED', symbol: 'AED', label: 'AED' },
  { code: 'SGD', symbol: 'S$', label: 'SGD (S$)' },
  { code: 'CAD', symbol: 'C$', label: 'CAD (C$)' },
  { code: 'AUD', symbol: 'A$', label: 'AUD (A$)' },
];

function getCurrencySymbol(currency: string): string {
  return CURRENCIES.find((c) => c.code === currency.toUpperCase())?.symbol || `${currency} `;
}

function formatPrice(cents: number, currency: string): string {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${(cents / 100).toFixed(2)}`;
}

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active':
      return 'default';
    case 'draft':
      return 'secondary';
    case 'archived':
      return 'outline';
    default:
      return 'secondary';
  }
}

// ── Query keys not yet in the shared qk factory ──
// Values match the existing cross-file usages verbatim (CommerceSettings,
// CategoryManagerDialog, BulkImportDialog, Orders) so invalidations stay in
// sync. NOTE: qk.commerceSettings resolves to ['commerce-settings', id], which
// does NOT match the de-facto ['ecommerce-settings', id] key used across the
// e-commerce pages — so we keep a local factory rather than orphan those.
// Promoted to the central factory — kept as aliases to minimize churn.
const ECOMMERCE_SETTINGS_KEY = qk.commerceSettings;
const ECOMMERCE_CATEGORIES_KEY = qk.ecommerceCategories;
const ECOMMERCE_DASHBOARD_KEY = qk.ecommerceDashboard;

// ── Component ──

export default function Products() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  // ── Filter & pagination state ──
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const limit = 10;

  // ── Dialog state ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [form, setForm] = useState<ProductFormData>(emptyForm);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [uploading, setUploading] = useState(false);
  // Set when the server rejects a save because the product changed under us
  // (409 VERSION_CONFLICT). Holds the server's current version so we can tell
  // the admin and offer a deliberate overwrite vs. discard-and-reload.
  const [conflict, setConflict] = useState<{ serverVersion: number } | null>(null);

  // ── Bulk import dialog ──
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

  // ── Archive confirm dialog ──
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archivingProduct, setArchivingProduct] = useState<Product | null>(null);

  // ── Bulk selection state ──
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [_bulkConfirmAction, setBulkConfirmAction] = useState<string>('');
  const [bulkTagInput, setBulkTagInput] = useState('');
  const [bulkTagDialogOpen, setBulkTagDialogOpen] = useState(false);
  const [bulkTagAction, setBulkTagAction] = useState<'add' | 'remove' | 'set'>('add');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Auto-generate slug from name when creating (not editing)
  useEffect(() => {
    if (!editingProduct && form.name) {
      setForm((prev) => ({ ...prev, slug: generateSlug(prev.name) }));
    }
  }, [form.name, editingProduct]);

  // ── Queries ──

  const basePath = `/projects/${projectId}/ecommerce`;

  /** Download a catalog CSV (template or export) as a file. */
  async function downloadCsv(
    kind: 'bulk/template' | 'bulk/export',
    filename: string,
    message: string
  ) {
    try {
      const res = await api.get(`${basePath}/products/${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(message);
    } catch {
      toast.error('Download failed');
    }
  }

  const {
    data: productsData,
    isPending: productsPending,
    isFetching: productsFetching,
    isError: productsError,
  } = useQuery({
    queryKey: [...qk.products(projectId), page, statusFilter, debouncedSearch, categoryFilter],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      if (categoryFilter !== 'all') params.categoryId = categoryFilter;
      const res = await api.get(`${basePath}/products`, { params });
      return res.data;
    },
    enabled: Boolean(projectId),
    // Keep the current page's rows on screen while the next page/filter loads,
    // so pagination and filter changes don't flash the skeleton.
    placeholderData: keepPreviousData,
  });

  const products: Product[] = productsData?.data || [];
  const meta: PaginationMeta = productsData?.meta || { page: 1, limit, total: 0, totalPages: 1 };

  const { data: categoriesData } = useQuery({
    queryKey: ECOMMERCE_CATEGORIES_KEY(projectId),
    queryFn: async () => {
      const res = await api.get(`${basePath}/categories`);
      return res.data;
    },
    enabled: Boolean(projectId),
  });

  const categories: Category[] = categoriesData?.data || [];

  const { data: storeSettingsData } = useQuery({
    queryKey: ECOMMERCE_SETTINGS_KEY(projectId),
    queryFn: async () => {
      const res = await api.get(`${basePath}/settings`);
      return res.data;
    },
    enabled: Boolean(projectId),
  });

  const storeCurrency: string = storeSettingsData?.data?.currency || 'INR';
  const storeTaxRate: number = storeSettingsData?.data?.taxRate ?? 0;
  const storeTaxName: string = storeSettingsData?.data?.taxName || 'Tax';

  const { data: dashboardData } = useQuery({
    queryKey: ECOMMERCE_DASHBOARD_KEY(projectId),
    queryFn: async () => {
      const res = await api.get(`${basePath}/dashboard`);
      return res.data;
    },
    enabled: Boolean(projectId),
  });

  const stats: DashboardStats = dashboardData?.data || {
    totalProducts: 0,
    activeProducts: 0,
    totalOrders: 0,
    revenue: 0,
    lowStockCount: 0,
  };

  // ── Mutations ──

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.post(`${basePath}/products`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.products(projectId) });
      queryClient.invalidateQueries({ queryKey: ECOMMERCE_DASHBOARD_KEY(projectId) });
      toast.success('Product created successfully');
      closeDialog();
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to create product'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await api.put(`${basePath}/products/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.products(projectId) });
      queryClient.invalidateQueries({ queryKey: ECOMMERCE_DASHBOARD_KEY(projectId) });
      toast.success('Product updated successfully');
      closeDialog();
    },
    onError: (err) => {
      if (isVersionConflict(err)) {
        // Keep the dialog open with the admin's edits and let them choose:
        // overwrite the newer server state, or discard and reload.
        setConflict({ serverVersion: conflictCurrentVersion(err) ?? 0 });
        return;
      }
      toast.error(apiErrorMessage(err, 'Failed to update product'));
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (productId: string) => {
      const res = await api.delete(`${basePath}/products/${productId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.products(projectId) });
      queryClient.invalidateQueries({ queryKey: ECOMMERCE_DASHBOARD_KEY(projectId) });
      toast.success('Product archived successfully');
      setArchiveDialogOpen(false);
      setArchivingProduct(null);
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to archive product'));
    },
  });

  // ── Bulk action mutations ──

  const invalidateBulk = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: qk.products(projectId) });
    queryClient.invalidateQueries({ queryKey: ECOMMERCE_DASHBOARD_KEY(projectId) });
    queryClient.invalidateQueries({ queryKey: ECOMMERCE_CATEGORIES_KEY(projectId) });
    setSelectedIds(new Set());
  }, [queryClient, projectId]);

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ productIds, status }: { productIds: string[]; status: string }) => {
      const res = await api.patch(`${basePath}/products/bulk/status`, { productIds, status });
      return res.data;
    },
    onSuccess: (data) => {
      invalidateBulk();
      toast.success(`${data.data.modifiedCount} product(s) updated`);
      setBulkConfirmOpen(false);
    },
    onError: () => toast.error('Failed to update status'),
  });

  const bulkCategoryMutation = useMutation({
    mutationFn: async ({
      productIds,
      categoryId,
    }: {
      productIds: string[];
      categoryId: string | null;
    }) => {
      const res = await api.patch(`${basePath}/products/bulk/category`, { productIds, categoryId });
      return res.data;
    },
    onSuccess: (data) => {
      invalidateBulk();
      toast.success(`${data.data.modifiedCount} product(s) recategorized`);
    },
    onError: () => toast.error('Failed to update category'),
  });

  const bulkTagsMutation = useMutation({
    mutationFn: async ({
      productIds,
      action,
      tags,
    }: {
      productIds: string[];
      action: string;
      tags: string[];
    }) => {
      const res = await api.patch(`${basePath}/products/bulk/tags`, { productIds, action, tags });
      return res.data;
    },
    onSuccess: (data) => {
      invalidateBulk();
      toast.success(`Tags updated on ${data.data.modifiedCount} product(s)`);
      setBulkTagDialogOpen(false);
      setBulkTagInput('');
    },
    onError: () => toast.error('Failed to update tags'),
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: async (productIds: string[]) => {
      const res = await api.delete(`${basePath}/products/bulk`, { data: { productIds } });
      return res.data;
    },
    onSuccess: (data) => {
      invalidateBulk();
      toast.success(`${data.data.modifiedCount} product(s) archived`);
      setBulkConfirmOpen(false);
    },
    onError: () => toast.error('Failed to archive products'),
  });

  const isBulkPending =
    bulkStatusMutation.isPending ||
    bulkCategoryMutation.isPending ||
    bulkTagsMutation.isPending ||
    bulkArchiveMutation.isPending;

  // ── Selection helpers ──

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map((p) => p._id)));
    }
  }

  const selectedArray = Array.from(selectedIds);

  // ── Handlers ──

  function closeDialog() {
    setDialogOpen(false);
    setEditingProduct(null);
    setForm(emptyForm);
    setImages([]);
    setConflict(null);
  }

  function openCreateDialog() {
    setEditingProduct(null);
    setForm(emptyForm);
    setImages([]);
    setDialogOpen(true);
  }

  function openEditDialog(product: Product) {
    setEditingProduct(product);
    setForm({
      name: product.name,
      slug: product.slug,
      description: product.description || '',
      type: product.type,
      status: product.status === 'archived' ? 'draft' : product.status,
      price: (product.price / 100).toString(),
      compareAtPrice: product.compareAtPrice ? (product.compareAtPrice / 100).toString() : '',
      taxRate: product.taxRate != null ? product.taxRate.toString() : '',
      sku: product.sku || '',
      stock: (product.stock ?? 0).toString(),
      trackInventory: product.trackInventory ?? true,
      categoryId: product.categoryId || '',
      tags: (product.tags || []).join(', '),
    });
    setImages(product.images || []);
    setDialogOpen(true);
  }

  // ── Image handlers ──

  async function handleImageUpload(files: FileList | File[]) {
    setUploading(true);
    const fileArr = Array.from(files);
    const remaining = 20 - images.length;
    const toUpload = fileArr.slice(0, remaining);

    for (const file of toUpload) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post(`${basePath}/media`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60_000,
        });
        const { url } = res.data.data;
        setImages((prev) => [
          ...prev,
          {
            url,
            alt: file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '),
            order: prev.length,
            isPrimary: prev.length === 0,
          },
        ]);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
  }

  function removeImage(index: number) {
    setImages((prev) => {
      const next = prev.filter((_, i) => i !== index).map((img, i) => ({ ...img, order: i }));
      // Ensure one image is primary
      if (next.length > 0 && !next.some((img) => img.isPrimary)) {
        next[0].isPrimary = true;
      }
      return next;
    });
  }

  function setPrimaryImage(index: number) {
    setImages((prev) => prev.map((img, i) => ({ ...img, isPrimary: i === index })));
  }

  function moveImage(from: number, to: number) {
    setImages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((img, i) => ({ ...img, order: i }));
    });
  }

  function openArchiveDialog(product: Product) {
    setArchivingProduct(product);
    setArchiveDialogOpen(true);
  }

  function handleSubmit(force = false) {
    if (!form.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    const priceValue = parseFloat(form.price);
    if (isNaN(priceValue) || priceValue < 0) {
      toast.error('Please enter a valid price');
      return;
    }

    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      slug: form.slug || generateSlug(form.name),
      description: form.description.trim(),
      type: form.type,
      status: form.status,
      price: Math.round(priceValue * 100),
      sku: form.sku.trim(),
      stock: parseInt(form.stock, 10) || 0,
      trackInventory: form.trackInventory,
    };

    if (form.compareAtPrice) {
      const comparePrice = parseFloat(form.compareAtPrice);
      if (!isNaN(comparePrice) && comparePrice > 0) {
        payload.compareAtPrice = Math.round(comparePrice * 100);
      }
    }

    // taxRate: empty = let backend inherit from store settings; set = override
    if (form.taxRate !== '') {
      const taxVal = parseFloat(form.taxRate);
      if (!isNaN(taxVal) && taxVal >= 0 && taxVal <= 100) {
        payload.taxRate = taxVal;
      }
    }

    if (form.categoryId && form.categoryId !== '') {
      payload.categoryId = form.categoryId;
    }

    if (form.tags.trim()) {
      payload.tags = form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
    }

    // Attach images
    payload.images = images.map((img, i) => ({
      url: img.url,
      alt: img.alt || '',
      order: i,
      isPrimary: img.isPrimary,
    }));

    if (editingProduct) {
      // Optimistic concurrency: declare the version we loaded so the server
      // rejects (409) if someone else saved in the meantime. `force` — chosen
      // by the admin after acknowledging a conflict — omits the guard to
      // deliberately overwrite the newer server state.
      if (!force) {
        payload.expectedVersion = editingProduct.version ?? 0;
      }
      updateMutation.mutate({ id: editingProduct._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // ── Helpers for rendering ──

  function getCategoryName(categoryId: string): string {
    const cat = categories.find((c) => c._id === categoryId);
    return cat?.name || '\u2014';
  }

  function getPrimaryImage(product: Product): string | null {
    if (!product.images || product.images.length === 0) return null;
    const primary = product.images.find((img) => img.isPrimary);
    return (primary || product.images[0])?.url || null;
  }

  // ── Render ──

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/projects/${projectId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Products</h1>
            <p className="text-muted-foreground text-sm">Manage your product catalog</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCategoryManagerOpen(true)}>
            <FolderOpen className="mr-2 h-4 w-4" />
            Categories
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Upload className="mr-2 h-4 w-4" />
                Import / Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setBulkImportOpen(true)}>
                <Upload className="mr-2 h-4 w-4" /> Bulk import products &amp; images…
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  void downloadCsv(
                    'bulk/template',
                    'product-import-template.csv',
                    'Template downloaded — example rows are ignored on import'
                  )
                }
              >
                <Download className="mr-2 h-4 w-4" /> Download import template
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  void downloadCsv(
                    'bulk/export',
                    'products-export.csv',
                    'Catalog exported — edit and re-import to bulk-update'
                  )
                }
              >
                <Download className="mr-2 h-4 w-4" /> Export catalog (CSV for Excel)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Products</CardTitle>
            <Package className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalProducts}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Products</CardTitle>
            <Tag className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeProducts}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Low Stock</CardTitle>
            <Archive className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.lowStockCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenue</CardTitle>
            <DollarSign className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(stats.revenue, storeCurrency)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[160px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={categoryFilter}
          onValueChange={(v) => {
            setCategoryFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat._id} value={cat._id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && (
        <div className="bg-muted/50 flex items-center gap-2 rounded-lg border px-4 py-2.5 shadow-sm">
          <CheckSquare className="text-primary h-4 w-4" />
          <span className="text-sm font-medium">{selectedIds.size} selected</span>

          <div className="bg-border ml-2 h-4 w-px" />

          {/* Status dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isBulkPending}>
                Set Status
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() =>
                  bulkStatusMutation.mutate({ productIds: selectedArray, status: 'active' })
                }
              >
                <Badge variant="default" className="mr-2 bg-green-600 text-xs">
                  Active
                </Badge>
                Mark as Active
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  bulkStatusMutation.mutate({ productIds: selectedArray, status: 'draft' })
                }
              >
                <Badge variant="secondary" className="mr-2 text-xs">
                  Draft
                </Badge>
                Mark as Draft
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Category dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isBulkPending}>
                <FolderOpen className="mr-1.5 h-3.5 w-3.5" />
                Set Category
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() =>
                  bulkCategoryMutation.mutate({ productIds: selectedArray, categoryId: null })
                }
              >
                No Category
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {categories.map((cat) => (
                <DropdownMenuItem
                  key={cat._id}
                  onClick={() =>
                    bulkCategoryMutation.mutate({ productIds: selectedArray, categoryId: cat._id })
                  }
                >
                  {cat.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Tags */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isBulkPending}>
                <Tags className="mr-1.5 h-3.5 w-3.5" />
                Tags
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  setBulkTagAction('add');
                  setBulkTagDialogOpen(true);
                }}
              >
                Add Tags
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setBulkTagAction('remove');
                  setBulkTagDialogOpen(true);
                }}
              >
                Remove Tags
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setBulkTagAction('set');
                  setBulkTagDialogOpen(true);
                }}
              >
                Replace All Tags
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="ml-auto flex items-center gap-2">
            {/* Archive */}
            <Button
              variant="destructive"
              size="sm"
              disabled={isBulkPending}
              onClick={() => {
                setBulkConfirmAction('archive');
                setBulkConfirmOpen(true);
              }}
            >
              <Archive className="mr-1.5 h-3.5 w-3.5" />
              Archive
            </Button>

            {/* Deselect */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={isBulkPending}
            >
              <X className="mr-1 h-3.5 w-3.5" />
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Products Table */}
      <Card>
        <CardContent className="relative p-0">
          {/* Quiet background-refresh hint — never blanks the current rows. */}
          {productsFetching && !productsPending && <RefreshingHint />}
          {productsPending ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
            </div>
          ) : productsError ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Package className="text-muted-foreground mb-3 h-10 w-10" />
              <p className="text-muted-foreground text-sm">
                Failed to load products. Please try again.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => queryClient.invalidateQueries({ queryKey: qk.products(projectId) })}
              >
                Retry
              </Button>
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Package className="text-muted-foreground mb-3 h-10 w-10" />
              <h3 className="text-sm font-medium">No products found</h3>
              <p className="text-muted-foreground mt-1 text-sm">
                {debouncedSearch || statusFilter !== 'all' || categoryFilter !== 'all'
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding your first product.'}
              </p>
              {!debouncedSearch && statusFilter === 'all' && categoryFilter === 'all' && (
                <Button size="sm" className="mt-4" onClick={openCreateDialog}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Product
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px] pl-4">
                      <Checkbox
                        checked={products.length > 0 && selectedIds.size === products.length}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead className="w-[50px]">Image</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Stock</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => {
                    const imageUrl = getPrimaryImage(product);
                    const isSelected = selectedIds.has(product._id);
                    return (
                      <TableRow key={product._id} className={isSelected ? 'bg-muted/50' : ''}>
                        <TableCell className="pl-4">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(product._id)}
                            aria-label={`Select ${product.name}`}
                          />
                        </TableCell>
                        <TableCell>
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={product.name}
                              className="h-10 w-10 rounded-md object-cover"
                            />
                          ) : (
                            <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-md">
                              <ImageIcon className="text-muted-foreground h-4 w-4" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{product.name}</p>
                            {product.sku && (
                              <p className="text-muted-foreground text-xs">SKU: {product.sku}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {product.categoryId ? getCategoryName(product.categoryId) : '\u2014'}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {formatPrice(product.price, storeCurrency)}
                            </p>
                            {product.compareAtPrice > 0 &&
                              product.compareAtPrice > product.price && (
                                <p className="text-muted-foreground text-xs line-through">
                                  {formatPrice(product.compareAtPrice, storeCurrency)}
                                </p>
                              )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {product.trackInventory ? (
                            <span
                              className={
                                product.stock <= (product.lowStockThreshold || 5)
                                  ? 'font-medium text-red-600'
                                  : ''
                              }
                            >
                              {product.stock}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">\u221e</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusBadgeVariant(product.status)}>
                            {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(product)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => openArchiveDialog(product)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Archive
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t px-4 py-3">
                <p className="text-muted-foreground text-sm">
                  Showing {(meta.page - 1) * meta.limit + 1}
                  {' \u2013 '}
                  {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} products
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-muted-foreground text-sm">
                    Page {meta.page} of {meta.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= meta.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
            <DialogDescription>
              {editingProduct
                ? 'Update the product details below.'
                : 'Fill in the details to create a new product.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Name */}
            <div className="grid gap-2">
              <Label htmlFor="product-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="product-name"
                placeholder="Product name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            {/* Slug */}
            <div className="grid gap-2">
              <Label htmlFor="product-slug">Slug</Label>
              <Input
                id="product-slug"
                placeholder="product-slug"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
              <p className="text-muted-foreground text-xs">
                Auto-generated from name. Edit only if you need a custom URL slug.
              </p>
            </div>

            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="product-description">Description</Label>
              <Textarea
                id="product-description"
                placeholder="Detailed product description..."
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            {/* ── Product Images ── */}
            <div className="grid gap-2">
              <Label>
                Images{' '}
                <span className="text-muted-foreground font-normal">({images.length}/20)</span>
              </Label>

              {/* Thumbnail grid */}
              {images.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, i) => (
                    <div
                      key={img.url}
                      className={`group relative overflow-hidden rounded-md border-2 ${
                        img.isPrimary
                          ? 'border-primary ring-primary/20 ring-2'
                          : 'border-muted hover:border-muted-foreground/30'
                      }`}
                    >
                      <img
                        src={img.url}
                        alt={img.alt}
                        className="aspect-square w-full object-cover"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('text/plain', i.toString())}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
                          if (!isNaN(from) && from !== i) moveImage(from, i);
                        }}
                      />
                      {/* Primary badge */}
                      {img.isPrimary && (
                        <span className="bg-primary text-primary-foreground absolute left-1 top-1 rounded px-1.5 py-0.5 text-[10px] font-medium">
                          Primary
                        </span>
                      )}
                      {/* Hover actions */}
                      <div className="absolute inset-0 flex items-end justify-center gap-1 bg-black/40 p-1 opacity-0 transition-opacity group-hover:opacity-100">
                        {!img.isPrimary && (
                          <button
                            type="button"
                            onClick={() => setPrimaryImage(i)}
                            className="rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-medium text-black hover:bg-white"
                          >
                            Set Primary
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="rounded bg-red-600/90 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Drop zone / upload button */}
              {images.length < 20 && (
                <label
                  className="border-muted-foreground/25 bg-muted/30 hover:border-primary/40 hover:bg-muted/50 flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('border-primary/60');
                  }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('border-primary/60')}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-primary/60');
                    if (e.dataTransfer.files.length) handleImageUpload(e.dataTransfer.files);
                  }}
                >
                  {uploading ? (
                    <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                  ) : (
                    <ImageIcon className="text-muted-foreground h-5 w-5" />
                  )}
                  <span className="text-muted-foreground text-sm">
                    {uploading ? 'Uploading...' : 'Drop images here or click to browse'}
                  </span>
                  <span className="text-muted-foreground/60 text-xs">
                    JPEG, PNG, WebP, GIF — max 10 MB each
                  </span>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.length) handleImageUpload(e.target.files);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}

              {images.length > 1 && (
                <p className="text-muted-foreground text-xs">
                  Drag thumbnails to reorder. First image or primary is used as the product cover.
                </p>
              )}
            </div>

            {/* Type & Status row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm({ ...form, type: v as ProductFormData['type'] })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical">Physical</SelectItem>
                    <SelectItem value="digital">Digital</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) =>
                    setForm({ ...form, status: v as ProductFormData['status'] })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Price row (currency is store-wide — set in Commerce Settings) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="product-price">
                  Price ({storeCurrency}) <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <span className="text-muted-foreground pointer-events-none absolute left-3 top-0 flex h-9 items-center text-sm">
                    {getCurrencySymbol(storeCurrency)}
                  </span>
                  <Input
                    id="product-price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="pl-8"
                  />
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="product-compare-price">Compare At Price</Label>
                <div className="relative">
                  <span className="text-muted-foreground pointer-events-none absolute left-3 top-0 flex h-9 items-center text-sm">
                    {getCurrencySymbol(storeCurrency)}
                  </span>
                  <Input
                    id="product-compare-price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={form.compareAtPrice}
                    onChange={(e) => setForm({ ...form, compareAtPrice: e.target.value })}
                    className="pl-8"
                  />
                </div>
                <p className="text-muted-foreground text-xs">
                  Original price for showing a discount.
                </p>
              </div>
            </div>

            {/* Tax Rate (override) */}
            <div className="grid gap-1.5">
              <Label htmlFor="product-tax-rate">{storeTaxName} Rate (%) — Override</Label>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  id="product-tax-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder={`${storeTaxRate}% (store default)`}
                  value={form.taxRate}
                  onChange={(e) => setForm({ ...form, taxRate: e.target.value })}
                />
                {form.taxRate !== '' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-fit text-xs"
                    onClick={() => setForm({ ...form, taxRate: '' })}
                  >
                    Reset to store default ({storeTaxRate}%)
                  </Button>
                )}
              </div>
              <p className="text-muted-foreground text-xs">
                Leave empty to use the store default ({storeTaxName} {storeTaxRate}%). Set a value
                to override for this product.
              </p>
            </div>

            {/* SKU & Stock row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="product-sku">SKU</Label>
                <Input
                  id="product-sku"
                  placeholder="SKU-001"
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="product-stock">Stock</Label>
                <Input
                  id="product-stock"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                />
              </div>
            </div>

            {/* Track Inventory */}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="track-inventory" className="cursor-pointer">
                  Track Inventory
                </Label>
                <p className="text-muted-foreground text-xs">
                  Enable stock tracking and low-stock alerts for this product.
                </p>
              </div>
              <Switch
                id="track-inventory"
                checked={form.trackInventory}
                onCheckedChange={(checked) => setForm({ ...form, trackInventory: checked })}
              />
            </div>

            {/* Category */}
            <div className="grid gap-2">
              <Label>Category</Label>
              <Select
                value={form.categoryId || 'none'}
                onValueChange={(v) => setForm({ ...form, categoryId: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tags */}
            <div className="grid gap-2">
              <Label htmlFor="product-tags">Tags</Label>
              <Input
                id="product-tags"
                placeholder="tag1, tag2, tag3"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
              />
              <p className="text-muted-foreground text-xs">
                Comma-separated list of tags for filtering and search.
              </p>
            </div>
          </div>

          {/* ── Concurrency conflict banner ──
              Shown when a save was rejected because the product changed on the
              server while this admin was editing. We keep their unsaved edits
              and let them choose how to reconcile. */}
          {conflict && (
            <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">
                    This product changed elsewhere while you were editing.
                  </p>
                  <p className="text-amber-800">
                    The server now has version {conflict.serverVersion} (you loaded{' '}
                    {editingProduct?.version ?? 0}). Your edits are still here — overwrite to keep
                    them, or discard to load the latest.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Discard local edits: close the dialog and refetch so the
                    // list (and the next edit) shows the latest server state.
                    closeDialog();
                    queryClient.invalidateQueries({ queryKey: qk.products(projectId) });
                  }}
                >
                  Discard &amp; load latest
                </Button>
                <Button size="sm" disabled={isSaving} onClick={() => handleSubmit(true)}>
                  Overwrite with my changes
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={() => handleSubmit()} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingProduct ? 'Save Changes' : 'Create Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      {projectId && (
        <BulkImportDialog
          projectId={projectId}
          open={bulkImportOpen}
          onOpenChange={setBulkImportOpen}
        />
      )}

      {/* Category Manager Dialog */}
      {projectId && (
        <CategoryManagerDialog
          projectId={projectId}
          open={categoryManagerOpen}
          onOpenChange={setCategoryManagerOpen}
        />
      )}

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Product</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive{' '}
              <span className="font-medium">{archivingProduct?.name}</span>? The product will be
              hidden from your storefront but can be restored later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setArchiveDialogOpen(false);
                setArchivingProduct(null);
              }}
              disabled={archiveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (archivingProduct) {
                  archiveMutation.mutate(archivingProduct._id);
                }
              }}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Archive Confirmation Dialog */}
      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Archive {selectedIds.size} Product{selectedIds.size > 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to archive{' '}
              <span className="font-medium">
                {selectedIds.size} product{selectedIds.size > 1 ? 's' : ''}
              </span>
              ? They will be hidden from your storefront but can be restored later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkConfirmOpen(false)}
              disabled={bulkArchiveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => bulkArchiveMutation.mutate(selectedArray)}
              disabled={bulkArchiveMutation.isPending}
            >
              {bulkArchiveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Archive {selectedIds.size} Product{selectedIds.size > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Tag Dialog */}
      <Dialog open={bulkTagDialogOpen} onOpenChange={setBulkTagDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {bulkTagAction === 'add'
                ? 'Add Tags'
                : bulkTagAction === 'remove'
                  ? 'Remove Tags'
                  : 'Replace Tags'}
            </DialogTitle>
            <DialogDescription>
              {bulkTagAction === 'add'
                ? `Add tags to ${selectedIds.size} selected product(s).`
                : bulkTagAction === 'remove'
                  ? `Remove tags from ${selectedIds.size} selected product(s).`
                  : `Replace all tags on ${selectedIds.size} selected product(s).`}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <Label htmlFor="bulk-tags">Tags (comma-separated)</Label>
            <Input
              id="bulk-tags"
              placeholder="tag1, tag2, tag3"
              value={bulkTagInput}
              onChange={(e) => setBulkTagInput(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBulkTagDialogOpen(false);
                setBulkTagInput('');
              }}
              disabled={bulkTagsMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const tags = bulkTagInput
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean);
                if (tags.length === 0) {
                  toast.error('Please enter at least one tag');
                  return;
                }
                bulkTagsMutation.mutate({ productIds: selectedArray, action: bulkTagAction, tags });
              }}
              disabled={bulkTagsMutation.isPending || !bulkTagInput.trim()}
            >
              {bulkTagsMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {bulkTagAction === 'add'
                ? 'Add Tags'
                : bulkTagAction === 'remove'
                  ? 'Remove Tags'
                  : 'Replace Tags'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
